import {
  AccountCircle,
  BuildOutlined,
  CompareArrows,
  KeyboardBackspace,
  LocationCity,
  QuestionAnswerSharp,
  Smartphone,
} from '@mui/icons-material';

import AccountBalanceIcon from '@/icons/AccountBalanceIcon';
import ChartPieIcon from '@/icons/ChartPieIcon';
import CustomUserGroupIcon from '@/icons/CustomUserGroupIcon';
import CustomUsersIcon from '@/icons/CustomUsersIcon';
import DocumentTextIcon from '@/icons/DocumentTextIcon';
import HeroSolidSwitchHorizontalIcon from '@/icons/HeroSolidSwitchHorizontalIcon';
import ImageSearchIcon from '@/icons/ImageSearchIcon';
import TemplateIcon from '@/icons/TemplateIcon';
import CashIcon from '@mui/icons-material/AttachMoney';
import CampaignIcon from '@mui/icons-material/Campaign';
import HandymanIcon from '@mui/icons-material/Handyman';

import { BITSTOP_OPERATOR_ID } from '@/constants/app/config';
import type { SidebarItemDataExtended } from '@/types';

/**
 * Sidebar configuration
 * * Sidebar items can be configured here, we can redirect the links internally externally or redirect them to Nonce V1 prepending the NONCE_V1_HOST constant
 *
 * * Each element can have multiple sub items using the `items` prop.
 *
 * ! This is only for the sidebar configuration, if you want to hide a specific view you will need to remove it from the router too. (view.tsx)
 */
export const SIDEBAR_ITEMS: SidebarItemDataExtended[] = [
  {
    icon: TemplateIcon,
    title: 'Dashboard',
    href: '/',
  },
  {
    icon: HeroSolidSwitchHorizontalIcon,
    title: 'Sales',
    href: '/sales/transactions',
    permission: 'operator_settings.view',
  },
  {
    icon: Smartphone,
    title: 'Machines',
    href: '/machines/atms',
    permission: 'machines.view',
  },
  {
    icon: CashIcon,
    title: 'Cash Logs',
    href: '/cash-logs',
    permission: 'machines.view',
  },
  {
    icon: CustomUsersIcon,
    title: 'Customers',
    href: '/customers',
    permission: 'customers.view',
  },
  {
    icon: LocationCity,
    title: 'Locations',
    href: '/locations',
    permission: 'locations.view',
  },
  {
    icon: CampaignIcon,
    title: 'Marketing',
    permission: 'location_queue.view',
    operatorId: BITSTOP_OPERATOR_ID,
    items: [
      {
        title: 'Dashboard',
        href: '/marketing',
      },
      {
        title: 'Listings',
        href: '/marketing/listings',
      },
      {
        title: 'Coupons',
        href: '/marketing/coupons',
        permission: 'coupons.view',
      },
    ],
  },
  {
    icon: AccountBalanceIcon,
    title: 'Wallet',
    href: '/wallet',
    permission: 'wallets.view',
  },
  {
    icon: AccountBalanceIcon,
    title: 'Wallets',
    href: '/wallets',
    permission: 'wallets.view',
  },
  {
    icon: ImageSearchIcon,
    title: 'Compliance',
    href: '/compliance',
    permission: 'kyc.view',
  },
  {
    icon: DocumentTextIcon,
    title: 'Invoices',
    href: '/invoices',
    permission: 'invoices.view',
  },
  {
    icon: ChartPieIcon,
    title: 'Stats',
    href: '/stats',
    permission: 'customer_statistics.view',
  },
  {
    icon: AccountCircle,
    title: 'Users',
    href: '/users',
    auth0Href: '/users-v2',
    permission: 'users.view',
  },
  {
    icon: CustomUserGroupIcon,
    title: 'Affiliates',
    href: '/affiliates',
    permission: 'affiliates.view',
  },
  {
    icon: QuestionAnswerSharp,
    title: 'Technical Support',
    href: 'https://bitstoptechnicalsupport.zendesk.com/hc/en-us',
    external: true,
    externalIcon: true,
  },
  {
    icon: HandymanIcon,
    title: 'Operations',
    permission: 'operations.view',
    items: [
      {
        title: 'Merchants',
        href: '/merchants',
      },
      {
        title: 'Issues',
        href: '/issues',
      },
      {
        title: 'Cases',
        href: '/cases',
      },
      {
        title: 'Service Tickets',
        href: '/service-tickets',
      },
      {
        title: 'Restocking',
        href: '/restocking',
      },
      {
        title: 'Routes',
        href: '/routes',
      },
      {
        title: 'Configs',
        href: '/configs',
      },
    ],
  },
];

export const SIDEBAR_ITEMS_SETTINGS: SidebarItemDataExtended[] = [
  {
    icon: KeyboardBackspace,
    title: 'Settings',
    href: '/',
  },
  {
    icon: CompareArrows,
    title: 'Integrations',
    items: [
      {
        title: 'Blockchain Analytics',
        href: '/settings/integrations/blockchain-analytics',
      },
    ],
  },
  {
    icon: BuildOutlined,
    title: 'Maintenance',
    items: [
      {
        title: 'Provisioning QR code',
        href: '/settings/maintenance/provisioning-qr-code',
      },
    ],
  },
];
